import { DateTime } from 'luxon';

export type CalendarSectionKind = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat';

export type CalendarSection = {
  kind: CalendarSectionKind;
  day: number;
};

export type CalendarEntry = {
  value: DateTime;
  date: number;
  key: string;
};

export type AvailabilityCalendar = {
  focus: DateTime;
  sections: CalendarSection[];
  entries: CalendarEntry[];
  chunks: Array<Array<CalendarEntry | undefined>>;
  title: () => string;
  next: () => void;
  prev: () => void;
  reset: () => void;
};

const SECTION_KINDS: CalendarSectionKind[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const createEntry = (value: DateTime): CalendarEntry => ({
  value,
  date: value.day,
  key: value.toFormat('yyyy-MM-dd'),
});

export const initializeAvailabilityCalendar = (): AvailabilityCalendar => {
  const calendar: AvailabilityCalendar = {
    focus: DateTime.utc(),
    sections: SECTION_KINDS.map((kind, index) => ({ kind, day: index })),
    entries: [],
    chunks: [],
    title: () => calendar.focus.toFormat('MMMM yyyy'),
    reset: () => {
      const focus = calendar.focus;
      const start = focus.startOf('month');
      const until = focus.endOf('month');

      const entries: CalendarEntry[] = [];
      for (let day = start.day; day <= until.day; day++) {
        entries.push(createEntry(focus.set({ day })));
      }

      const startPadding = Math.max(0, start.weekday % 7);
      const endPadding = Math.max(0, 6 - (until.weekday % 7));

      const padded: Array<CalendarEntry | undefined> = [
        ...Array.from({ length: startPadding }, () => undefined),
        ...entries,
        ...Array.from({ length: endPadding }, () => undefined),
      ];

      const chunks: Array<Array<CalendarEntry | undefined>> = [];
      for (let index = 0; index < padded.length; index += calendar.sections.length) {
        chunks.push(padded.slice(index, index + calendar.sections.length));
      }

      calendar.entries = entries;
      calendar.chunks = chunks;
    },
    next: () => {
      calendar.focus = calendar.focus.plus({ months: 1 });
      calendar.reset();
    },
    prev: () => {
      calendar.focus = calendar.focus.minus({ months: 1 });
      calendar.reset();
    },
  };

  calendar.reset();
  return calendar;
};
